import styled from '@emotion/styled';
import { Button, Modal } from 'antd';
import { TFunction } from 'i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API, TAssetLink, TAssetLinkStats } from '../../api';
import { LocaleKeys } from '../../locale';
import { AssetKind } from '../../pages/module1-data';
import { useAnalyticsFilterSelector } from '../../state/analytics/selectors';
import { timeToString } from '../../utils/common';
import { Table } from '../table';
import { TSortValue, TableColumn, TableRowData } from '../table/types';
import { TableHeader } from './analytics-table/table-header-component';
import { TableLabelComponent } from './analytics-table/table-label-component';
import { TableProgress } from './analytics-table/table-progress';

// Mirrors the video analytics table's row shape (name, time, views,
// uniqueUsers as the file analog of totalViews, the 5 indexes) plus a single
// Completed column. Coverage/pauseReason/events/indexEvents are intentionally
// not part of this UI-facing row type anymore — the underlying telemetry
// they were derived from is still fetched and used for the computations
// below, it's just no longer rendered.
type TFileAnalyticsRow = {
  name: string;
  type: string;
  time: string;
  views: number | string;
  uniqueUsers: number | string;
  attentiveness: number;
  engagement: number;
  emotionality: number;
  fatigue: number;
  absent_mindedness: number;
  completed: string;
  email?: string;
  details: string;
  is_hidden_users_stats?: boolean;
};

type TIndexKey =
  | 'attentiveness'
  | 'engagement'
  | 'emotionality'
  | 'fatigue'
  | 'absent_mindedness';

type TFileIndexAverages = Record<TIndexKey, number>;

type TFileAnalyticsDetails = {
  title: string;
  type: string;
  views: number | string;
  uniqueUsers: number | string;
  time: string;
  completed: string;
  indexes: TFileIndexAverages;
};

const MILLISECONDS_IN_SECOND = 1000;

const Wrapper = styled.div`
  margin-top: 24px;
`;

const Title = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 20px;
  line-height: 1.25;
`;

const DetailsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled('div')`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._300};
`;

const DetailLabel = styled('div')`
  margin-bottom: 6px;
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 12px;
`;

const DetailValue = styled('div')`
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 15px;
  font-weight: 600;
`;

const isDocumentLink = (assetLink: TAssetLink) => assetLink.kind !== 'video';

const getShortSessionId = (sessionId: string) => sessionId.slice(0, 8);

const EMPTY_INDEX_AVERAGES: TFileIndexAverages = {
  absent_mindedness: 0,
  attentiveness: 0,
  emotionality: 0,
  engagement: 0,
  fatigue: 0,
};

const INDEX_RENDER_PROPS: Record<TIndexKey, { color: string; labelKey: string }> =
  {
    absent_mindedness: {
      color: '#FA5555',
      labelKey: 'table.columns.absent_mindednessLabel',
    },
    attentiveness: {
      color: '#1871F8',
      labelKey: 'table.columns.attentivenessLabel',
    },
    emotionality: {
      color: '#D8B126',
      labelKey: 'table.columns.emotionalityLabel',
    },
    engagement: {
      color: '#B46AEE',
      labelKey: 'table.columns.engagementLabel',
    },
    fatigue: {
      color: '#888C9D',
      labelKey: 'table.columns.fatigueLabel',
    },
  };

const normalizeIndexPercent = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  const normalized = value <= 2 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(normalized)));
};

const readIndexValue = (
  indexes: Record<string, unknown> | null,
  keys: string[]
) => {
  if (!indexes) return null;

  for (const key of keys) {
    const value = normalizeIndexPercent(indexes[key]);
    if (value !== null) return value;
  }

  return null;
};

const getEventIndexes = (
  event: TAssetLinkEvent
): Partial<TFileIndexAverages> => ({
  absent_mindedness:
    readIndexValue(event.indexes, ['absent_mindedness', 'distraction', 'amazement']) ??
    undefined,
  attentiveness:
    readIndexValue(event.indexes, ['attentiveness', 'attention']) ?? undefined,
  emotionality:
    readIndexValue(event.indexes, ['emotionality', 'happiness', 'emotions']) ??
    undefined,
  engagement:
    readIndexValue(event.indexes, ['engagement', 'involvement']) ?? undefined,
  fatigue: readIndexValue(event.indexes, ['fatigue', 'fatique', 'tiredness']) ?? undefined,
});

const getAverageIndexes = (
  events: TAssetLinkEvent[]
): TFileIndexAverages => {
  const totals = { ...EMPTY_INDEX_AVERAGES };
  const counts = { ...EMPTY_INDEX_AVERAGES };

  events.forEach((event) => {
    const indexes = getEventIndexes(event);

    (Object.keys(totals) as TIndexKey[]).forEach((key) => {
      const value = indexes[key];
      if (typeof value !== 'number') return;

      totals[key] += value;
      counts[key] += 1;
    });
  });

  return (Object.keys(totals) as TIndexKey[]).reduce(
    (result, key) => ({
      ...result,
      [key]: counts[key] > 0 ? Math.round(totals[key] / counts[key]) : 0,
    }),
    { ...EMPTY_INDEX_AVERAGES }
  );
};

type TDocumentSession = NonNullable<
  TAssetLinkStats['documentSessions']
>[number];
type TAssetLinkEvent = TAssetLinkStats['events'][number];

const isMeaningfulDocumentSession = (session: TDocumentSession) =>
  session.completed ||
  session.qualifiedDurationMs > 0 ||
  session.coveragePercent > 0;

const getEventDocumentSessionId = (event: TAssetLinkEvent) => {
  const value = event.metadata?.documentProgress;

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const progress = value as Record<string, unknown>;
  return typeof progress.sessionId === 'string' ? progress.sessionId : null;
};

const getKindLabel = (kind: AssetKind) => {
  switch (kind) {
    case 'excel':
      return 'Excel';
    case 'document':
      return 'Word';
    case 'text':
      return 'Text';
    case 'csv':
      return 'CSV';
    case 'pdf':
      return 'PDF';
    case 'video':
    default:
      return 'Video';
  }
};

// Computes everything needed for one parameterized link's own row plus its
// session (leaf) rows. Kept as a pure function (no JSX/table wrapping) so the
// same numbers can be re-aggregated one level up into the file (grouped by
// s3Key) row without re-deriving them from raw events twice.
const computeLinkAnalytics = (
  item: TAssetLinkStats,
  periodStart: number | undefined,
  periodEnd: number | undefined,
  // When non-empty, restricts every downstream aggregate to the selected
  // users so the file table can produce a per-student report. Sessions/events
  // without a matching userId simply fall out (a null userId can't match).
  userIdFilter: Set<string> | null,
  t: TFunction<string[]>
) => {
  const periodEvents = item.events.filter((event) => {
    if (userIdFilter && (!event.userId || !userIdFilter.has(event.userId))) {
      return false;
    }

    const eventTime = new Date(event.createdAt).getTime();

    if (periodStart && eventTime < periodStart) {
      return false;
    }

    if (periodEnd && eventTime > periodEnd) {
      return false;
    }

    return true;
  });

  const hasPeriod = !!periodStart || !!periodEnd;
  const userScopedSessions = (item.documentSessions ?? []).filter(
    (session) =>
      !userIdFilter || (!!session.userId && userIdFilter.has(session.userId))
  );
  const periodSessions = userScopedSessions.filter((session) => {
    const eventTime = new Date(session.lastEventAt).getTime();
    return (
      (!periodStart || eventTime >= periodStart) &&
      (!periodEnd || eventTime <= periodEnd)
    );
  });
  const documentSessions = hasPeriod ? periodSessions : userScopedSessions;
  const meaningfulDocumentSessions = documentSessions.filter(
    isMeaningfulDocumentSession
  );
  const excludedSessionIds = new Set(
    documentSessions
      .filter((session) => !isMeaningfulDocumentSession(session))
      .map((session) => session.sessionId)
  );
  const hasDocumentTelemetry = documentSessions.length > 0;
  // When any scoping filter (date period or user) is active, precomputed
  // backend aggregates (item.viewsCount/uniqueViewersCount/viewingDuration)
  // are for the whole link and unfiltered, so derive from the filtered
  // events instead. Only with no scoping at all do we trust the item.* totals.
  const hasScopingFilter = hasPeriod || !!userIdFilter;
  const filteredEvents = hasDocumentTelemetry
    ? periodEvents.filter((event) => {
        const sessionId = getEventDocumentSessionId(event);
        return !sessionId || !excludedSessionIds.has(sessionId);
      })
    : periodEvents;
  const views = hasDocumentTelemetry
    ? meaningfulDocumentSessions.length
    : hasScopingFilter
      ? periodEvents.filter((event) => event.type === 'open').length
      : item.viewsCount;
  const viewingDuration = hasDocumentTelemetry
    ? meaningfulDocumentSessions.reduce(
        (sum, session) => sum + session.qualifiedDurationMs,
        0
      )
    : hasScopingFilter
      ? periodEvents.reduce(
          (maximum, event) => Math.max(maximum, event.viewingDuration),
          0
        )
      : item.viewingDuration;
  const viewingDurationMs = hasDocumentTelemetry
    ? viewingDuration
    : viewingDuration * MILLISECONDS_IN_SECOND;
  const uniqueUsers = hasDocumentTelemetry
    ? new Set(
        meaningfulDocumentSessions
          .map((session) => session.userId)
          .filter(Boolean)
      ).size
    : hasScopingFilter
      ? new Set(periodEvents.map((event) => event.userId).filter(Boolean))
          .size
      : item.uniqueViewersCount;
  const linkIndexes = getAverageIndexes(filteredEvents);
  const completedCount = meaningfulDocumentSessions.filter(
    (session) => session.completed
  ).length;
  // Distinct users for file-level (grouped-by-s3Key) deduplication. The
  // link-level `uniqueUsers`/`completed` above stay per-link; these arrays
  // let the file row take a UNION across links so a user who used two links
  // to the same file is counted once.
  const userIds = Array.from(
    new Set(
      meaningfulDocumentSessions
        .map((session) => session.userId)
        .filter((userId): userId is string => Boolean(userId))
    )
  );
  const completedUserIds = Array.from(
    new Set(
      meaningfulDocumentSessions
        .filter((session) => session.completed)
        .map((session) => session.userId)
        .filter((userId): userId is string => Boolean(userId))
    )
  );

  const sessionRows = meaningfulDocumentSessions.map((session) => {
    const sessionEvents = filteredEvents.filter(
      (event) => getEventDocumentSessionId(event) === session.sessionId
    );
    const sessionIndexes = getAverageIndexes(sessionEvents);

    return {
      id: `${item.assetLink.id}:${session.sessionId}`,
      data: {
        name: `${t('fileTable.session')} ${getShortSessionId(
          session.sessionId
        )} / ${
          session.userFullName
            ? `${session.userFullName}${
                session.userEmail ? ` (${session.userEmail})` : ''
              }`
            : session.userEmail ||
              session.userId ||
              t('fileTable.unknownUser')
        }`,
        type: `${session.speed}x`,
        time: timeToString(
          Math.round(session.qualifiedDurationMs / MILLISECONDS_IN_SECOND)
        ),
        views: '-',
        uniqueUsers: '-',
        ...sessionIndexes,
        // Leaf rows show a plain completion badge — no coverage %, matching
        // the video reference which has no completeness indicator either.
        completed: session.completed ? '✓' : '—',
        email: session.userEmail || undefined,
        details: t('fileTable.details'),
      },
    };
  });

  return {
    s3Key: item.assetLink.s3Key,
    fileName: item.assetLink.name,
    kind: item.assetLink.kind,
    linkId: item.assetLink.id,
    data: {
      name: item.assetLink.title || item.assetLink.name,
      type: getKindLabel(item.assetLink.kind),
      time: timeToString(
        Math.round(
          hasDocumentTelemetry
            ? viewingDuration / MILLISECONDS_IN_SECOND
            : viewingDuration
        )
      ),
      views,
      uniqueUsers,
      ...linkIndexes,
      // Aggregate rows show completed sessions as a fraction of views.
      completed: `${completedCount} / ${typeof views === 'number' ? views : 0}`,
      details: t('fileTable.details'),
    },
    viewsNumeric: typeof views === 'number' ? views : 0,
    viewingDurationMs,
    uniqueUsersNumeric: typeof uniqueUsers === 'number' ? uniqueUsers : 0,
    completedCount,
    userIds,
    completedUserIds,
    filteredEvents,
    sessionRows,
  };
};

const getColumns = (
  t: TFunction<string[]>,
  openDetails: (details: TFileAnalyticsDetails) => void
): TableColumn<TFileAnalyticsRow>[] => [
  {
    key: 'name',
    label: t('fileTable.columns.name'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (_, data, expanded, canBeExpanded, childIndex) => (
      <TableLabelComponent
        label={data.name}
        expanded={expanded}
        canBeExpanded={canBeExpanded}
        childIndex={childIndex}
        linkStatTooltip={t('table.linkStatTooltip')}
        email={data.email}
      />
    ),
  },
  {
    key: 'time',
    label: t('fileTable.columns.time'),
    columnComponent: TableHeader,
    sortable: true,
  },
  {
    key: 'views',
    label: t('fileTable.columns.views'),
    columnComponent: TableHeader,
    sortable: true,
  },
  {
    key: 'uniqueUsers',
    label: t('fileTable.columns.uniqueUsers'),
    columnComponent: TableHeader,
    sortable: true,
  },
  {
    key: 'attentiveness',
    label: t('table.columns.attentivenessLabel'),
    tooltip: t('table.columns.attentivenessTooltip'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (value) => (
      <TableProgress value={value as number} color={INDEX_RENDER_PROPS.attentiveness.color} />
    ),
  },
  {
    key: 'engagement',
    label: t('table.columns.engagementLabel'),
    tooltip: t('table.columns.engagementTooltip'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (value) => (
      <TableProgress value={value as number} color={INDEX_RENDER_PROPS.engagement.color} />
    ),
  },
  {
    key: 'emotionality',
    label: t('table.columns.emotionalityLabel'),
    tooltip: t('table.columns.emotionalityTooltip'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (value) => (
      <TableProgress value={value as number} color={INDEX_RENDER_PROPS.emotionality.color} />
    ),
  },
  {
    key: 'fatigue',
    label: t('table.columns.fatigueLabel'),
    tooltip: t('table.columns.fatigueTooltip'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (value) => (
      <TableProgress value={value as number} color={INDEX_RENDER_PROPS.fatigue.color} />
    ),
  },
  {
    key: 'absent_mindedness',
    label: t('table.columns.absent_mindednessLabel'),
    tooltip: t('table.columns.absent_mindednessTooltip'),
    columnComponent: TableHeader,
    sortable: true,
    renderFunc: (value) => (
      <TableProgress
        value={value as number}
        color={INDEX_RENDER_PROPS.absent_mindedness.color}
      />
    ),
  },
  {
    key: 'completed',
    label: t('fileTable.columns.completed'),
    columnComponent: TableHeader,
    sortable: true,
  },
  {
    key: 'details',
    label: t('fileTable.columns.details'),
    columnComponent: TableHeader,
    renderFunc: (_, data) => (
      <Button
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          openDetails({
            completed: data.completed,
            indexes: {
              absent_mindedness: data.absent_mindedness,
              attentiveness: data.attentiveness,
              emotionality: data.emotionality,
              engagement: data.engagement,
              fatigue: data.fatigue,
            },
            time: data.time,
            title: data.name,
            type: data.type,
            uniqueUsers: data.uniqueUsers,
            views: data.views,
          });
        }}
      >
        {t('fileTable.details')}
      </Button>
    ),
  },
];

const toSortableNumber = (value: number | string): number =>
  typeof value === 'number' ? value : parseInt(value, 10) || 0;

const parseCompletedSortValue = (value: string): number => {
  if (value === '✓') return 1;
  if (value === '—') return 0;

  const numerator = parseInt(value.split('/')[0], 10);
  return Number.isFinite(numerator) ? numerator : 0;
};

const sortFileAnalyticsRows = (
  a: TableRowData<TFileAnalyticsRow>,
  b: TableRowData<TFileAnalyticsRow>,
  sortedData: Partial<Record<keyof TFileAnalyticsRow, TSortValue>>
): number => {
  const key = (Object.keys(sortedData) as (keyof TFileAnalyticsRow)[])[0];
  const direction = key ? sortedData[key] : undefined;

  if (!direction) return 0;

  switch (key) {
    case 'name':
      return direction === 'asc'
        ? a.data.name.localeCompare(b.data.name)
        : b.data.name.localeCompare(a.data.name);
    case 'time':
      return direction === 'asc'
        ? parseInt(a.data.time, 10) - parseInt(b.data.time, 10)
        : parseInt(b.data.time, 10) - parseInt(a.data.time, 10);
    case 'views':
    case 'uniqueUsers':
      return direction === 'asc'
        ? toSortableNumber(a.data[key]) - toSortableNumber(b.data[key])
        : toSortableNumber(b.data[key]) - toSortableNumber(a.data[key]);
    case 'completed':
      return direction === 'asc'
        ? parseCompletedSortValue(a.data.completed) -
            parseCompletedSortValue(b.data.completed)
        : parseCompletedSortValue(b.data.completed) -
            parseCompletedSortValue(a.data.completed);
    case 'absent_mindedness':
    case 'attentiveness':
    case 'engagement':
    case 'fatigue':
    case 'emotionality':
      return direction === 'asc'
        ? (a.data[key] ?? 0) - (b.data[key] ?? 0)
        : (b.data[key] ?? 0) - (a.data[key] ?? 0);
    default:
      return 0;
  }
};

export const FileAnalyticsTable: FC = () => {
  const { t } = useTranslation([LocaleKeys.ANALYTICS]);
  const { assetLinkId, userId, playlistId, startDate, endDate } =
    useAnalyticsFilterSelector();
  const [stats, setStats] = useState<TAssetLinkStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortedData, setSortedData] = useState<
    Partial<Record<keyof TFileAnalyticsRow, TSortValue>>
  >({});
  const [details, setDetails] = useState<TFileAnalyticsDetails | null>(null);
  // Link ids contained by the selected playlists/courses. null = no playlist
  // filter active (show every link, as before). A course and a plain playlist
  // both resolve uniformly through their items[].linkId, so this is just the
  // union of link ids across the selected ids.
  const [playlistLinkSet, setPlaylistLinkSet] = useState<Set<string> | null>(
    null
  );

  useEffect(() => {
    if (!playlistId || playlistId.length === 0) {
      setPlaylistLinkSet(null);
      return;
    }

    let isActive = true;

    Promise.all(playlistId.map((id) => API.playlists.getById(id)))
      .then((responses) => {
        if (!isActive) {
          return;
        }

        const linkIds = new Set<string>();
        responses.forEach(({ data }) => {
          (data.items ?? []).forEach((item) => {
            if (item.linkId) {
              linkIds.add(item.linkId);
            }
          });
        });
        setPlaylistLinkSet(linkIds);
      })
      .catch(() => {
        // Fall back to "no playlist constraint" rather than crashing the table.
        if (isActive) {
          setPlaylistLinkSet(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, [playlistId]);

  useEffect(() => {
    let isActive = true;

    setLoading(true);
    API.assetLinks
      .getSelf()
      .then(async ({ data }) => {
        const documentLinks = data.filter(isDocumentLink);
        const assetLinkSelected =
          assetLinkId && assetLinkId.length > 0
            ? documentLinks.filter((el) => assetLinkId.includes(el.id))
            : documentLinks;
        // Intersect with the playlist/course link set when active — this
        // narrows on top of the existing assetLinkId filter, never replaces it.
        const selectedLinks = playlistLinkSet
          ? assetLinkSelected.filter((el) => playlistLinkSet.has(el.id))
          : assetLinkSelected;

        const statsResponses = await Promise.all(
          selectedLinks.map((el) => API.assetLinks.getStats(el.id))
        );

        if (isActive) {
          setStats(statsResponses.map((el) => el.data));
        }
      })
      .catch(() => {
        if (isActive) {
          setStats([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [assetLinkId, playlistLinkSet]);

  const columns = useMemo(() => getColumns(t, setDetails), [t]);

  const rows: TableRowData<TFileAnalyticsRow>[] = useMemo(() => {
    const periodStart = startDate ? new Date(startDate).getTime() : undefined;
    const periodEnd = endDate ? new Date(endDate).getTime() : undefined;
    // Non-empty -> per-student report: restrict every aggregate to these
    // users and prune rows that end up empty. Empty/undefined -> unchanged.
    const userIdFilter =
      userId && userId.length > 0 ? new Set(userId) : null;

    const allLinkAnalytics = stats.map((item) =>
      computeLinkAnalytics(item, periodStart, periodEnd, userIdFilter, t)
    );
    // With a user filter active, drop links that yielded no sessions/views
    // for the selected users so the table reads as a per-student report.
    // A file group with all-empty links then produces no map entry below and
    // disappears on its own. Without a filter, keep every link unchanged.
    const linkAnalytics = userIdFilter
      ? allLinkAnalytics.filter(
          (link) => link.viewsNumeric > 0 || link.sessionRows.length > 0
        )
      : allLinkAnalytics;

    // Group parameterized links by their underlying file (s3Key) so the
    // table gets the same 3-level shape as the video table: file -> link ->
    // session, instead of the old flat link -> session structure.
    const fileGroups = new Map<string, typeof linkAnalytics>();
    linkAnalytics.forEach((link) => {
      const group = fileGroups.get(link.s3Key);
      if (group) {
        group.push(link);
      } else {
        fileGroups.set(link.s3Key, [link]);
      }
    });

    const result: TableRowData<TFileAnalyticsRow>[] = [];

    fileGroups.forEach((links, s3Key) => {
      const [first] = links;
      const totalViews = links.reduce((sum, link) => sum + link.viewsNumeric, 0);
      const totalDurationMs = links.reduce(
        (sum, link) => sum + link.viewingDurationMs,
        0
      );
      // "uniqueUsers" must mean distinct PEOPLE at the file level: union the
      // userIds across the file's links so a user who opened two links to the
      // same file is counted once (summing per-link uniques double-counts).
      const distinctUserIds = new Set(
        links.flatMap((link) => link.userIds)
      );
      const distinctCompletedUserIds = new Set(
        links.flatMap((link) => link.completedUserIds)
      );
      const fileIndexes = getAverageIndexes(
        links.flatMap((link) => link.filteredEvents)
      );

      result.push({
        id: `file:${s3Key}`,
        data: {
          name: first.fileName,
          type: getKindLabel(first.kind),
          time: timeToString(
            Math.round(totalDurationMs / MILLISECONDS_IN_SECOND)
          ),
          views: totalViews,
          uniqueUsers: distinctUserIds.size,
          ...fileIndexes,
          // Distinct people who completed >=1 link over distinct people who
          // viewed the file (not summed per-link sessions).
          completed: `${distinctCompletedUserIds.size} / ${distinctUserIds.size}`,
          details: t('fileTable.details'),
        },
        children: links.map((link) => ({
          id: link.linkId,
          data: { ...link.data, is_hidden_users_stats: false },
          children: link.sessionRows.map((session) => ({
            id: session.id,
            data: { ...session.data, is_hidden_users_stats: false },
          })),
        })),
      });
    });

    if (sortedData && Object.keys(sortedData).length > 0) {
      result.sort((a, b) => sortFileAnalyticsRows(a, b, sortedData));
      result.forEach((fileRow) => {
        fileRow.children?.sort((a, b) => sortFileAnalyticsRows(a, b, sortedData));
        fileRow.children?.forEach((linkRow) => {
          linkRow.children?.sort((a, b) =>
            sortFileAnalyticsRows(a, b, sortedData)
          );
        });
      });
    }

    return result;
  }, [endDate, startDate, stats, sortedData, t, userId]);

  const handleSetSortedData = useCallback(
    (data: Partial<Record<keyof TFileAnalyticsRow, TSortValue>>) => {
      setSortedData(data);
    },
    []
  );

  return (
    <Wrapper>
      <Title>{t('fileTable.title')}</Title>
      <Table
        columns={columns}
        rows={rows}
        loading={loading}
        emptyText={t('table.tableEmpty')}
        sortedData={sortedData}
        setSortedData={handleSetSortedData}
      />
      <Modal
        open={Boolean(details)}
        title={details?.title}
        footer={null}
        onCancel={() => setDetails(null)}
      >
        {details && (
          <DetailsGrid>
            <DetailItem>
              <DetailLabel>{t('fileTable.columns.type')}</DetailLabel>
              <DetailValue>{details.type}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('fileTable.columns.time')}</DetailLabel>
              <DetailValue>{details.time}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('fileTable.columns.views')}</DetailLabel>
              <DetailValue>{details.views}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('fileTable.columns.uniqueUsers')}</DetailLabel>
              <DetailValue>{details.uniqueUsers}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('fileTable.columns.completed')}</DetailLabel>
              <DetailValue>{details.completed}</DetailValue>
            </DetailItem>
            {(Object.keys(INDEX_RENDER_PROPS) as TIndexKey[]).map((key) => (
              <DetailItem key={key}>
                <DetailLabel>{t(INDEX_RENDER_PROPS[key].labelKey)}</DetailLabel>
                <TableProgress
                  value={details.indexes[key]}
                  color={INDEX_RENDER_PROPS[key].color}
                />
              </DetailItem>
            ))}
          </DetailsGrid>
        )}
      </Modal>
    </Wrapper>
  );
};
